import { useQueries, useQuery } from "@tanstack/react-query";

import type { Counterparty } from "../entities/counterparties";
import type { ShellServices } from "./services";

type CounterpartyService = NonNullable<ShellServices["counterparties"]>;

export const counterpartyKeys = {
  all: ["document-workflow", "counterparties"] as const,
  directory: ["document-workflow", "counterparties", "directory"] as const,
  detail: (id: string) =>
    ["document-workflow", "counterparties", "detail", id] as const,
  rules: (counterpartyId: string | null) =>
    ["document-workflow", "counterparties", "rules", counterpartyId] as const,
  inbox: (state: string) =>
    ["document-workflow", "counterparties", "inbox", state] as const,
};

/**
 * Названия контрагентов по идентификаторам. Правила и письма ссылаются на
 * контрагента только идентификатором; первая сотня справочника читается одним
 * запросом, а тех, кого в ней нет, дочитываем по одному.
 */
export function useCounterpartyNames(
  service: CounterpartyService,
  ids: readonly string[],
  { enabled = true }: { enabled?: boolean } = {},
): {
  names: ReadonlyMap<string, string>;
  options: readonly Counterparty[];
  loading: boolean;
} {
  const directory = useQuery({
    queryKey: counterpartyKeys.directory,
    queryFn: () => service.list({ per_page: 100 }),
    staleTime: 30_000,
    enabled,
  });
  const known = new Map(
    (directory.data?.data ?? []).map((item) => [item.id, item.name]),
  );
  const missing = directory.isSuccess
    ? [...new Set(ids)].filter((id) => !known.has(id))
    : [];
  const extra = useQueries({
    queries: missing.map((id) => ({
      queryKey: counterpartyKeys.detail(id),
      queryFn: () => service.get(id),
      staleTime: 30_000,
    })),
  });
  for (const result of extra) {
    if (result.data !== undefined) known.set(result.data.id, result.data.name);
  }
  return {
    names: known,
    options: directory.data?.data ?? [],
    loading: directory.isLoading,
  };
}
